import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PCInstance, Room, RoomPlayspace, PlanPoint } from '../types/database';
import {
  createPCAgentClient, HmdPoseRow, HmdPosesData, MpPositionsData, PCAgentClient,
} from '../services/pcAgent';
import { databaseService } from '../services/database';
import { OperatorMessage } from '../services/operatorMessages';
import SendVisitorMessageModal from './SendVisitorMessageModal';
import Toast, { ToastData } from './Toast';
import '../styles/playspace-map.css';

/**
 * Playspace map: every player in the room drawn on the room plan, live.
 *
 * Primary source: the room's RealityServer. The game on every PC sends its HMD
 * pose to it 30 times a second, so the server knows where every seat is, no
 * matter what runs on the headset. The browser cannot speak UDP to the NUC,
 * so the map asks any reachable room PC's agent (GET /api/mp/positions), which
 * bridges the question over the LAN. The primary is asked first; when it does
 * not answer, the next PC is tried.
 *
 * Fallback source, per seat: the headset's own tracking via the PC agents
 * (GET /api/hmd/poses), which exists only where the headset shell app is
 * installed. It covers a seat before the game is launched or after it died
 * without a shadow. Polling that endpoint is what keeps the shell streaming,
 * so closing this modal stops it.
 *
 * Coordinates. The server reports Unreal world units (cm, degrees); the
 * headset reports its local tracking frame (metres). Both map onto the plan
 * through the same measured relation (5-point walk, DEV1, 2026-08-24):
 *   plan x = -local.x = -ue.y/100,   plan y = -local.z = ue.x/100,
 *   heading = local yaw = -ue yaw,   origin = tracking origin, 1:1.
 * The experience anchors its world at that origin, so a player drawn here
 * stands where the game thinks they stand.
 */

interface PlayspaceMapProps {
  room: Room;
  pcInstances: PCInstance[];
  /** Tapping a player opens "Bericht naar bezoeker" (same gate as the PC tile). */
  canSendVisitorMessage: boolean;
  onClose: () => void;
}

const SERVER_POLL_MS = 300; // one PC, cached 100 ms on the agent
const HMD_POLL_MS = 500; // every PC, only the fallback
const STALE_MS = 2500; // a pose older than this is drawn faded
const TRAIL_MS = 8000; // how much of each player's path stays visible
const UNREACHABLE_MS = 5000;
const PALETTE = ['#3fb950', '#4ea1ff', '#f0883e', '#d946ef', '#facc15', '#22d3ee', '#f85149', '#a371f7'];

const hmdToPlan = (p: { x: number; z: number }): PlanPoint => ({ x: -p.x, y: -p.z });
const ueToPlan = (p: { x: number; y: number }): PlanPoint => ({ x: -p.y / 100, y: p.x / 100 });

type Source = 'game' | 'shadow' | 'headset';

type Verdict =
  | 'on_map'
  | 'off_head'
  | 'not_relocalised'
  | 'not_in_session'
  | 'no_position'
  | 'connecting';

const VERDICT_TEXT: Record<Verdict, string> = {
  on_map: 'Op de kaart',
  off_head: 'Headset niet op het hoofd',
  not_relocalised: 'Headset herkent de ruimte nog niet',
  not_in_session: 'Spel stuurt geen positie (niet in de sessie)',
  no_position: 'Geen positie',
  connecting: 'Verbinden…',
};

const SOURCE_TEXT: Record<Source, string> = {
  game: 'via het spel',
  shadow: 'via de headset-schaduw (spel stil)',
  headset: 'via de headset',
};

type ServerStatus = 'connecting' | 'ok' | 'pc_unreachable' | 'no_server_ip' | 'server_unreachable' | 'server_too_old' | 'agent_too_old';

const SERVER_TEXT: Record<ServerStatus, string> = {
  connecting: 'Server: verbinden…',
  ok: '',
  pc_unreachable: 'Geen PC in deze ruimte bereikbaar',
  no_server_ip: 'Geen multiplayer-server ingesteld voor deze ruimte',
  server_unreachable: 'Server antwoordt niet (uit, of ouder dan 1.3.7)',
  server_too_old: 'Server te oud voor de kaart (update naar 1.3.7)',
  agent_too_old: 'Agent te oud voor de kaart (update de PC’s)',
};

interface ServerSample {
  at: number;
  status: ServerStatus;
  data: MpPositionsData | null;
  viaPc: string | null;
}

interface PcSample {
  at: number;
  data: HmdPosesData | null;
  ok: boolean;
}

interface Player {
  key: string;
  name: string;
  seat: number;
  color: string;
  verdict: Verdict;
  source: Source | null;
  plan: PlanPoint | null;
  yaw: number;
  stale: boolean;
  /** The PC behind this player (absent for strays), for actions such as a visitor message. */
  pc?: PCInstance;
}

interface TrailPoint {
  t: number;
  p: PlanPoint;
}

/** Where a marker was drawn, in CSS pixels, for tap hit-testing. */
interface MarkerHit {
  key: string;
  x: number;
  y: number;
  r: number;
}

// Freshest headset pose per serial across every PC's answer (the primary
// holds stray sockets for the whole room).
function freshestRows(samples: Map<string, PcSample>, now: number): Map<string, { row: HmdPoseRow; ageMs: number }> {
  const out = new Map<string, { row: HmdPoseRow; ageMs: number }>();
  samples.forEach((s) => {
    if (!s.data) return;
    for (const row of s.data.headsets) {
      const serial = row.serial.toUpperCase();
      const ageMs = now - s.at + (row.pose ? row.age_ms ?? 0 : Number.MAX_SAFE_INTEGER / 2);
      const prev = out.get(serial);
      if (!prev || ageMs < prev.ageMs) out.set(serial, { row, ageMs });
    }
  });
  return out;
}

function resolvePlayers(
  pcs: PCInstance[],
  server: ServerSample | null,
  hmdSamples: Map<string, PcSample>,
  assignedSerialByPc: Map<string, string>,
  now: number,
): { players: Player[]; strays: Player[] } {
  const rows = freshestRows(hmdSamples, now);
  const claimedSerials = new Set<string>();
  const claimedSeats = new Set<number>();
  const sorted = [...pcs].sort((a, b) => (a.player_index ?? 99) - (b.player_index ?? 99));
  const serverOk = server?.status === 'ok' && server.data !== null;
  const serverAge = server ? now - server.at : Number.MAX_SAFE_INTEGER;
  const seatRows = new Map<number, MpPositionsData['seats'][number]>();
  if (serverOk) for (const s of server!.data!.seats) seatRows.set(s.seat, s);

  const players: Player[] = sorted.map((pc, i) => {
    const hmd = hmdSamples.get(pc.id);
    // The seat is pc_instances.player_index (the agent announces the same
    // number to the server); the agent's own word wins when it is known.
    const seat = (hmd?.data?.seat ?? 0) > 0 ? hmd!.data!.seat : pc.player_index ?? i + 1;
    claimedSeats.add(seat);
    const color = PALETTE[i % PALETTE.length];
    const live = hmd?.data?.paired_serial?.toUpperCase() || null;
    const serial = live || assignedSerialByPc.get(pc.id) || null;
    if (serial) claimedSerials.add(serial);
    const base = { key: pc.id, name: pc.pc_name || pc.id, seat, color, source: null, plan: null, yaw: 0, stale: false, pc };

    // 1. The server's view of this seat.
    const row = seatRows.get(seat);
    if (row && row.active) {
      return {
        ...base,
        verdict: 'on_map' as Verdict,
        source: (row.shadowed ? 'shadow' : 'game') as Source,
        plan: ueToPlan(row),
        yaw: -row.yaw,
        stale: serverAge + row.since_ms > STALE_MS,
      };
    }

    // 2. The headset's own tracking, where the shell exists.
    const found = serial ? rows.get(serial) : undefined;
    const pose = found?.row.pose;
    if (found && pose) {
      const { row: h, ageMs } = found;
      let verdict: Verdict = 'on_map';
      if (h.worn === false) verdict = 'off_head';
      else if (!h.relocalised) verdict = 'not_relocalised';
      return { ...base, verdict, source: 'headset' as Source, plan: hmdToPlan(pose), yaw: pose.yaw, stale: ageMs > STALE_MS };
    }

    // 3. Nothing places this player.
    if (!server || server.status === 'connecting') return { ...base, verdict: 'connecting' as Verdict };
    if (serverOk) return { ...base, verdict: 'not_in_session' as Verdict };
    return { ...base, verdict: 'no_position' as Verdict };
  });

  // Seats the server reports that belong to no PC in this room, and headsets
  // with a pose that no PC claims: drawn grey, listed separately.
  const strays: Player[] = [];
  if (serverOk) {
    for (const s of server!.data!.seats) {
      if (claimedSeats.has(s.seat) || !s.active) continue;
      strays.push({
        key: `seat:${s.seat}`, name: `Stoel ${s.seat}`, seat: s.seat, color: '#8b949e', verdict: 'on_map',
        source: s.shadowed ? 'shadow' : 'game', plan: ueToPlan(s), yaw: -s.yaw, stale: serverAge + s.since_ms > STALE_MS,
      });
    }
  }
  rows.forEach(({ row, ageMs }, serial) => {
    if (claimedSerials.has(serial) || !row.pose) return;
    strays.push({
      key: `hmd:${serial}`, name: `Headset …${serial.slice(-4)}`, seat: 0, color: '#8b949e',
      verdict: row.worn === false ? 'off_head' : !row.relocalised ? 'not_relocalised' : 'on_map',
      source: 'headset', plan: hmdToPlan(row.pose), yaw: row.pose.yaw, stale: ageMs > STALE_MS,
    });
  });
  return { players, strays };
}

// ---- drawing ---------------------------------------------------------------

function planBounds(space: RoomPlayspace | null, markers: Player[]) {
  let pts: PlanPoint[] = [];
  if (space) {
    pts = pts.concat(space.boundary);
    Object.values(space.zones).forEach((z) => (pts = pts.concat(z)));
    Object.values(space.walls).forEach((w) => (pts = pts.concat(w)));
    space.obstacles.forEach((o) => (pts = pts.concat(o)));
  }
  if (pts.length === 0) {
    pts = markers.filter((m) => m.plan).map((m) => m.plan!);
    pts.push({ x: 0, y: 0 });
  }
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const pad = 1.5;
  let x0 = Math.min(...xs) - pad;
  let x1 = Math.max(...xs) + pad;
  let y0 = Math.min(...ys) - pad;
  let y1 = Math.max(...ys) + pad;
  const minExtent = 8; // a plan-less room still gets a sensible window
  if (x1 - x0 < minExtent) {
    const c = (x0 + x1) / 2;
    x0 = c - minExtent / 2;
    x1 = c + minExtent / 2;
  }
  if (y1 - y0 < minExtent) {
    const c = (y0 + y1) / 2;
    y0 = c - minExtent / 2;
    y1 = c + minExtent / 2;
  }
  return { x0, x1, y0, y1 };
}

function drawMap(
  cv: HTMLCanvasElement,
  space: RoomPlayspace | null,
  players: Player[],
  strays: Player[],
  trails: Map<string, TrailPoint[]>,
  flip: boolean,
): MarkerHit[] {
  const hits: MarkerHit[] = [];
  const ctx = cv.getContext('2d');
  if (!ctx) return hits;
  const w = cv.width;
  const h = cv.height;
  const dpr = window.devicePixelRatio || 1;
  ctx.clearRect(0, 0, w, h);

  const all = players.concat(strays);
  const b = planBounds(space, all);
  const s = Math.min(w / (b.x1 - b.x0), h / (b.y1 - b.y0));
  const mx = (w - (b.x1 - b.x0) * s) / 2;
  const my = (h - (b.y1 - b.y0) * s) / 2;

  // Plan -> screen. Upright: plan +x runs left, plan +y runs up (RealityStudio's
  // orientation, north at the top). Flipped: the same plan turned 180 degrees.
  const S = (p: PlanPoint) =>
    flip
      ? { x: mx + (p.x - b.x0) * s, y: my + (p.y - b.y0) * s }
      : { x: mx + (b.x1 - p.x) * s, y: my + (b.y1 - p.y) * s };
  const px = (n: number) => n * dpr;
  const font = (size: number, bold = false) => `${bold ? 'bold ' : ''}${px(size)}px system-ui, sans-serif`;

  // 1 m grid.
  ctx.strokeStyle = '#1b2028';
  ctx.lineWidth = px(1);
  for (let x = Math.ceil(b.x0); x <= b.x1; x++) {
    const a = S({ x, y: b.y0 });
    const c = S({ x, y: b.y1 });
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(c.x, c.y);
    ctx.stroke();
  }
  for (let y = Math.ceil(b.y0); y <= b.y1; y++) {
    const a = S({ x: b.x0, y });
    const c = S({ x: b.x1, y });
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(c.x, c.y);
    ctx.stroke();
  }

  const polygon = (pts: PlanPoint[], close: boolean) => {
    ctx.beginPath();
    pts.forEach((p, i) => {
      const q = S(p);
      if (i) ctx.lineTo(q.x, q.y);
      else ctx.moveTo(q.x, q.y);
    });
    if (close) ctx.closePath();
  };

  if (space) {
    if (space.boundary.length >= 3) {
      polygon(space.boundary, true);
      ctx.fillStyle = 'rgba(78,161,255,.07)';
      ctx.fill();
      ctx.strokeStyle = '#4ea1ff';
      ctx.lineWidth = px(2);
      ctx.stroke();
    }
    const zoneColor: Record<string, string> = { playspace: '#d946ef', entrance: '#f85149', exit: '#a371f7' };
    for (const key of Object.keys(space.zones)) {
      const zn = space.zones[key];
      polygon(zn, true);
      ctx.strokeStyle = zoneColor[key] || '#8b949e';
      ctx.lineWidth = px(1.5);
      ctx.setLineDash([px(6), px(4)]);
      ctx.stroke();
      ctx.setLineDash([]);
      const c0 = zn.reduce((a, p) => ({ x: a.x + p.x / zn.length, y: a.y + p.y / zn.length }), { x: 0, y: 0 });
      const cs = S(c0);
      ctx.fillStyle = zoneColor[key] || '#8b949e';
      ctx.font = font(11);
      ctx.textAlign = 'center';
      ctx.fillText(key, cs.x, cs.y);
    }
    for (const o of space.obstacles) {
      polygon(o, true);
      ctx.fillStyle = 'rgba(139,148,158,.25)';
      ctx.fill();
      ctx.strokeStyle = '#8b949e';
      ctx.lineWidth = px(1);
      ctx.stroke();
    }
    for (const key of Object.keys(space.walls)) {
      polygon(space.walls[key], false);
      ctx.strokeStyle = '#f0883e';
      ctx.lineWidth = px(4);
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }

  // Tracking origin (the floor mark).
  const o = S({ x: 0, y: 0 });
  ctx.strokeStyle = '#f0883e';
  ctx.lineWidth = px(1.5);
  ctx.beginPath();
  ctx.moveTo(o.x - px(12), o.y);
  ctx.lineTo(o.x + px(12), o.y);
  ctx.moveTo(o.x, o.y - px(12));
  ctx.lineTo(o.x, o.y + px(12));
  ctx.stroke();

  // Compass on the plan's own edges (so it turns with the flip).
  ctx.fillStyle = '#e6edf3';
  ctx.font = font(13, true);
  ctx.textAlign = 'center';
  const top = S({ x: (b.x0 + b.x1) / 2, y: flip ? b.y0 : b.y1 });
  const bottom = S({ x: (b.x0 + b.x1) / 2, y: flip ? b.y1 : b.y0 });
  const left = S({ x: flip ? b.x0 : b.x1, y: (b.y0 + b.y1) / 2 });
  const right = S({ x: flip ? b.x1 : b.x0, y: (b.y0 + b.y1) / 2 });
  ctx.fillText(flip ? 'S' : 'N', top.x, top.y + px(16));
  ctx.fillText(flip ? 'N' : 'S', bottom.x, bottom.y - px(8));
  ctx.textAlign = 'left';
  ctx.fillText(flip ? 'E' : 'W', left.x + px(6), left.y + px(5));
  ctx.textAlign = 'right';
  ctx.fillText(flip ? 'W' : 'E', right.x - px(6), right.y + px(5));
  ctx.textAlign = 'left';

  // Trails, then markers on top.
  for (const m of all) {
    const tr = trails.get(m.key);
    if (!tr || tr.length < 2) continue;
    ctx.strokeStyle = m.color;
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = px(2);
    ctx.lineCap = 'round';
    ctx.beginPath();
    tr.forEach((tp, i) => {
      const q = S(tp.p);
      if (i) ctx.lineTo(q.x, q.y);
      else ctx.moveTo(q.x, q.y);
    });
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  for (const m of all) {
    if (!m.plan) continue;
    const p = S(m.plan);
    const muted = m.verdict === 'off_head';
    const warn = m.verdict === 'not_relocalised';
    const color = muted ? '#8b949e' : warn ? '#f0883e' : m.color;
    ctx.globalAlpha = m.stale ? 0.4 : 1;

    // Heading arrow: yaw 0 = facing north = screen up on the upright plan.
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate((-m.yaw * Math.PI) / 180 + (flip ? Math.PI : 0));
    ctx.beginPath();
    ctx.moveTo(0, -px(30));
    ctx.lineTo(px(10), -px(12));
    ctx.lineTo(-px(10), -px(12));
    ctx.closePath();
    ctx.fillStyle = color;
    if (muted) {
      ctx.strokeStyle = color;
      ctx.lineWidth = px(2);
      ctx.stroke();
    } else {
      ctx.fill();
    }
    ctx.restore();

    // Body: a numbered disc. Hollow when the headset is off the head; a
    // dashed rim when the seat is served by its shadow or the headset alone.
    const r = px(14);
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    if (muted) {
      ctx.fillStyle = '#12161c';
      ctx.fill();
      ctx.strokeStyle = color;
      ctx.lineWidth = px(2);
      ctx.stroke();
    } else {
      ctx.fillStyle = color;
      ctx.fill();
      if (warn || m.source !== 'game') {
        ctx.setLineDash([px(4), px(3)]);
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = px(2);
        ctx.stroke();
        ctx.setLineDash([]);
      }
    }
    ctx.fillStyle = muted ? color : '#0e1116';
    ctx.font = font(13, true);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(m.seat > 0 ? String(m.seat) : '?', p.x, p.y + px(0.5));
    ctx.textBaseline = 'alphabetic';

    ctx.fillStyle = '#e6edf3';
    ctx.font = font(12, true);
    ctx.fillText(m.name, p.x, p.y + r + px(15));
    ctx.textAlign = 'left';
    ctx.globalAlpha = 1;
    // Generous tap target (the arrow and the name count too).
    hits.push({ key: m.key, x: p.x / dpr, y: p.y / dpr, r: 30 });
  }
  return hits;
}

// ---- component -------------------------------------------------------------

function makeClients(pcs: PCInstance[]): Map<string, PCAgentClient> {
  const clients = new Map<string, PCAgentClient>();
  for (const pc of pcs) {
    const hosts = [pc.ip_address, pc.tailscale_ip].filter(Boolean) as string[];
    if (hosts.length > 0) clients.set(pc.id, createPCAgentClient(hosts, 9090, pc.id));
  }
  return clients;
}

function currentOperatorName(): string {
  try {
    const operatorData = sessionStorage.getItem('current_operator');
    if (!operatorData) return '';
    const operator = JSON.parse(operatorData);
    return operator.name || operator.operator_code || '';
  } catch {
    return '';
  }
}

export default function PlayspaceMap({ room, pcInstances, canSendVisitorMessage, onClose }: PlayspaceMapProps) {
  const [space, setSpace] = useState<RoomPlayspace | null | undefined>(undefined); // undefined = loading
  const [server, setServer] = useState<ServerSample | null>(null);
  const [hmdSamples, setHmdSamples] = useState<Map<string, PcSample>>(new Map());
  const [assigned, setAssigned] = useState<Map<string, string>>(new Map());
  const [flip, setFlip] = useState(false);
  const [now, setNow] = useState(Date.now());
  // Player picked for "Bericht naar bezoeker" (tap on a marker or a side row).
  const [messageTarget, setMessageTarget] = useState<Player | null>(null);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const trailsRef = useRef<Map<string, TrailPoint[]>>(new Map());
  const hitsRef = useRef<MarkerHit[]>([]);

  // The plan and the desired headset assignments, once.
  useEffect(() => {
    let cancelled = false;
    databaseService.getRoomPlayspace(room.id).then((ps) => {
      if (!cancelled) setSpace(ps);
    }).catch(() => {
      if (!cancelled) setSpace(null);
    });
    databaseService.getHmdDevicesForPCs(pcInstances.map((pc) => pc.id)).then((rows) => {
      if (cancelled) return;
      const m = new Map<string, string>();
      for (const r of rows) {
        if (r.paired_pc_instance_id && r.serial && !m.has(r.paired_pc_instance_id)) {
          m.set(r.paired_pc_instance_id, r.serial.toUpperCase());
        }
      }
      setAssigned(m);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [room.id, pcInstances]);

  // Server positions through one PC at a time: the primary first, then the
  // others in seat order. A PC that cannot be reached is skipped; a PC that
  // answers with a server problem is believed (every PC asks the same NUC).
  useEffect(() => {
    const clients = makeClients(pcInstances);
    const order = [...pcInstances]
      .filter((pc) => clients.has(pc.id))
      .sort((a, b) => (a.role === 'primary' ? -1 : b.role === 'primary' ? 1 : (a.player_index ?? 99) - (b.player_index ?? 99)));
    let idx = 0;
    let failuresInRow = 0;
    let inflight = false;
    let stopped = false;
    if (order.length === 0) {
      setServer({ at: Date.now(), status: 'pc_unreachable', data: null, viaPc: null });
      return;
    }
    const tick = async () => {
      if (stopped || inflight) return;
      inflight = true;
      const pc = order[idx % order.length];
      try {
        const res = await clients.get(pc.id)!.getMpPositions();
        if (stopped) return;
        failuresInRow = 0;
        if (res.ok) {
          setServer({ at: Date.now(), status: 'ok', data: res.data, viaPc: pc.pc_name || pc.id });
        } else if (res.error === 'agent_too_old') {
          // Another PC may already run a newer agent; keep looking.
          idx++;
          failuresInRow++;
          if (failuresInRow >= order.length) setServer({ at: Date.now(), status: 'agent_too_old', data: null, viaPc: null });
        } else {
          const status: ServerStatus =
            res.error === 'no_server_ip' || res.error === 'server_too_old' ? res.error : 'server_unreachable';
          setServer({ at: Date.now(), status, data: null, viaPc: pc.pc_name || pc.id });
        }
      } catch {
        if (stopped) return;
        idx++;
        failuresInRow++;
        if (failuresInRow >= order.length) {
          setServer((prev) => ({ at: Date.now(), status: 'pc_unreachable', data: prev?.status === 'ok' ? prev.data : null, viaPc: null }));
        }
      } finally {
        inflight = false;
      }
    };
    tick();
    const id = window.setInterval(tick, SERVER_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [pcInstances]);

  // Headset poses from every PC (the per-seat fallback). One request in
  // flight per PC; a slow PC never delays the others.
  useEffect(() => {
    const clients = makeClients(pcInstances);
    const inflight = new Set<string>();
    let stopped = false;
    const tick = () => {
      if (stopped) return;
      setNow(Date.now());
      clients.forEach((client, pcId) => {
        if (inflight.has(pcId)) return;
        inflight.add(pcId);
        client
          .getHmdPoses()
          .then((data) => {
            if (stopped) return;
            setHmdSamples((prev) => new Map(prev).set(pcId, { at: Date.now(), data, ok: true }));
          })
          .catch(() => {
            if (stopped) return;
            setHmdSamples((prev) => {
              const old = prev.get(pcId);
              return new Map(prev).set(pcId, { at: old?.ok ? old.at : Date.now() - UNREACHABLE_MS, data: old?.data ?? null, ok: false });
            });
          })
          .finally(() => inflight.delete(pcId));
      });
    };
    tick();
    const id = window.setInterval(tick, HMD_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(id);
    };
  }, [pcInstances]);

  const { players, strays } = useMemo(
    () => resolvePlayers(pcInstances, server, hmdSamples, assigned, now),
    [pcInstances, server, hmdSamples, assigned, now],
  );

  // Size the canvas to its stage (device pixels) and redraw on every change.
  useEffect(() => {
    const cv = canvasRef.current;
    const stage = stageRef.current;
    if (!cv || !stage) return;
    const dpr = window.devicePixelRatio || 1;
    const wantW = Math.round(stage.clientWidth * dpr);
    const wantH = Math.round(stage.clientHeight * dpr);
    if (cv.width !== wantW || cv.height !== wantH) {
      cv.width = wantW;
      cv.height = wantH;
    }
    const trails = trailsRef.current;
    const t = Date.now();
    for (const m of players.concat(strays)) {
      if (!m.plan || m.stale) continue;
      const tr = trails.get(m.key) ?? [];
      const last = tr[tr.length - 1];
      if (!last || last.p.x !== m.plan.x || last.p.y !== m.plan.y) tr.push({ t, p: m.plan });
      while (tr.length && t - tr[0].t > TRAIL_MS) tr.shift();
      trails.set(m.key, tr);
    }
    hitsRef.current = drawMap(cv, space ?? null, players, strays, trails, flip);
  }, [space, players, strays, flip, now]);

  // Which player (with a PC behind it) sits under a point on the canvas.
  const playerAt = (clientX: number, clientY: number): Player | null => {
    const cv = canvasRef.current;
    if (!cv) return null;
    const rect = cv.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    let best: MarkerHit | null = null;
    let bestD = Infinity;
    for (const h of hitsRef.current) {
      const d = Math.hypot(h.x - x, h.y - y);
      if (d <= h.r && d < bestD) {
        best = h;
        bestD = d;
      }
    }
    if (!best) return null;
    return players.find((p) => p.key === best!.key && p.pc) ?? null;
  };

  const pickPlayer = (p: Player | null) => {
    if (!p || !p.pc || !canSendVisitorMessage) return;
    setMessageTarget(p);
  };

  // Send the preset clip to the picked player's PC. Never throws: the modal
  // relies on that, feedback goes through the toast.
  const handleSendVisitorMessage = async (msg: OperatorMessage) => {
    const target = messageTarget;
    setMessageTarget(null);
    if (!target?.pc) return;
    const pc = target.pc;
    const hosts = [pc.ip_address, pc.tailscale_ip].filter(Boolean) as string[];
    if (hosts.length === 0 || pc.status !== 'online') {
      setToast({ kind: 'error', message: `${target.name} is niet bereikbaar` });
      return;
    }
    try {
      const result = await createPCAgentClient(hosts, 9090, pc.id).sendVisitorMessage({
        key: msg.key, text_nl: msg.text_nl, text_en: msg.text_en, clip_version: msg.clip_version, operator: currentOperatorName(),
      });
      if (result.success) setToast({ kind: 'success', message: `"${msg.label}" wordt afgespeeld op ${target.name}` });
      else setToast({ kind: 'error', message: result.error || `Bericht versturen naar ${target.name} mislukt` });
    } catch {
      setToast({ kind: 'error', message: `${target.name} is niet bereikbaar` });
    }
  };

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setNow(Date.now()));
    ro.observe(stage);
    return () => ro.disconnect();
  }, []);

  const visible = players.filter((p) => p.plan && p.verdict === 'on_map').length;
  const serverStatus: ServerStatus = server?.status ?? 'connecting';
  const serverNote = SERVER_TEXT[serverStatus];
  const serverOkNote = serverStatus === 'ok' && server?.data
    ? `Server ${server.data.server_version ? server.data.server_version + ' ' : ''}via ${server.viaPc}`
    : '';

  return (
    <div className="pmap-overlay">
      <div className="pmap-modal">
        <div className="pmap-header">
          <h3>
            🗺 Kaart · {room.name}
            <span className="pmap-header-sub">
              {visible} van {players.length} speler{players.length === 1 ? '' : 's'} op de kaart
            </span>
          </h3>
          <div className="pmap-header-actions">
            <button className={`pmap-btn${flip ? ' active' : ''}`} onClick={() => setFlip((f) => !f)} title="Draai de kaart 180 graden">
              ⟲ Draai kaart
            </button>
            <button className="pmap-btn close" onClick={onClose}>
              ✕ Sluiten
            </button>
          </div>
        </div>
        {serverNote && <div className={`pmap-banner${serverStatus === 'connecting' ? '' : ' warn'}`}>{serverNote}</div>}
        <div className="pmap-body">
          <div className="pmap-stage" ref={stageRef}>
            <canvas
              ref={canvasRef}
              style={{ cursor: hoverKey && canSendVisitorMessage ? 'pointer' : 'default' }}
              onClick={(e) => pickPlayer(playerAt(e.clientX, e.clientY))}
              onMouseMove={(e) => setHoverKey(playerAt(e.clientX, e.clientY)?.key ?? null)}
              onMouseLeave={() => setHoverKey(null)}
            />
            {space === null && (
              <div className="pmap-empty">
                Geen plattegrond voor deze ruimte.
                <br />
                Teken de playspace in RealityStudio; spelers worden intussen rond het tracking-nulpunt getoond.
              </div>
            )}
          </div>
          <aside className="pmap-side">
            <h4>Spelers</h4>
            {canSendVisitorMessage && <div className="pmap-hint">Tik op een speler voor "Bericht naar bezoeker".</div>}
            {players.map((p) => {
              const muted = p.plan === null || p.verdict === 'off_head';
              const tone = p.verdict === 'not_relocalised' || p.verdict === 'not_in_session' ? 'warn' : '';
              const tappable = canSendVisitorMessage && !!p.pc;
              return (
                <div
                  className={`pmap-row${tappable ? ' tappable' : ''}`}
                  key={p.key}
                  role={tappable ? 'button' : undefined}
                  onClick={() => pickPlayer(p)}
                  title={tappable ? 'Bericht naar bezoeker' : undefined}
                >
                  <div className={`pmap-dot${muted ? ' muted' : ''}`} style={{ background: p.color }}>
                    {p.seat}
                  </div>
                  <div className="pmap-row-text">
                    <div className="pmap-row-name">{p.name}</div>
                    <div className={`pmap-row-verdict ${tone}`}>
                      {VERDICT_TEXT[p.verdict]}
                      {p.source && p.plan ? ` · ${SOURCE_TEXT[p.source]}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
            {strays.length > 0 && (
              <>
                <h4>Overig in de ruimte</h4>
                {strays.map((p) => (
                  <div className="pmap-row" key={p.key}>
                    <div className="pmap-dot muted">{p.seat > 0 ? p.seat : '?'}</div>
                    <div className="pmap-row-text">
                      <div className="pmap-row-name">{p.name}</div>
                      <div className="pmap-row-verdict">
                        {VERDICT_TEXT[p.verdict]}
                        {p.source ? ` · ${SOURCE_TEXT[p.source]}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
            <div className="pmap-foot">
              {serverOkNote && <div>{serverOkNote}</div>}
              Positie komt van de multiplayer-server (het spel meldt zich daar), met de headset-tracking als reserve. Plattegrond uit RealityStudio; het oranje kruis is het nulpunt op de vloer.
            </div>
          </aside>
        </div>
      </div>
      {messageTarget && (
        <SendVisitorMessageModal
          contextLine={`${messageTarget.name} · speler ${messageTarget.seat} · ${VERDICT_TEXT[messageTarget.verdict]}`}
          onCancel={() => setMessageTarget(null)}
          onSubmit={handleSendVisitorMessage}
        />
      )}
      {toast && <Toast message={toast.message} kind={toast.kind} onClose={() => setToast(null)} />}
    </div>
  );
}
